import { useEffect, useState } from "react";
import { checkTableName, type TableNameCheck } from "../../../../../api/bills";

interface LiveCheckResult {
  /** Сервитьор + номер, за които е отговорът. */
  key: string;
  check: TableNameCheck | null;
  error: unknown;
}

/**
 * Проверката на номера, докато се пише (debounce 500 ms) — както в „Смяна на
 * маса": заета ли е масата и от кого. Само за показване под полето — самото
 * отваряне/разделяне си проверява наново (то е авторитетът), затова Enter не
 * чака тази проверка. Отговор за старо име (писало се е след него) не се
 * показва. `waiterId` — друг сервитьор (лична номерация: неговите маси), както
 * в самото отваряне.
 */
export function useTableNameLiveCheck({
  open,
  name,
  enabled,
  waiterId,
}: {
  open: boolean;
  name: string;
  enabled: boolean;
  waiterId?: number;
}) {
  const trimmed = name.trim();
  const checkKey = `${waiterId ?? ""}|${trimmed}`;
  const [result, setResult] = useState<LiveCheckResult | null>(null);
  const [checkingKey, setCheckingKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !enabled || !trimmed) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setCheckingKey(checkKey);
      try {
        const check = await checkTableName(trimmed, undefined, waiterId);
        if (!cancelled) setResult({ key: checkKey, check, error: null });
      } catch (error) {
        if (!cancelled) setResult({ key: checkKey, check: null, error });
      } finally {
        if (!cancelled) setCheckingKey(null);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, enabled, trimmed, waiterId, checkKey]);

  const current = open && enabled && result?.key === checkKey ? result : null;
  return {
    isChecking: open && enabled && !!trimmed && checkingKey === checkKey,
    /** Заета маса — с името на сервитьора, ако облакът го е казал. */
    taken: current?.check?.exists ? { takenBy: current.check.takenBy } : null,
    /** Проверката не мина (без връзка/грешка) — само предупреждение. */
    failed: current?.error ?? null,
  };
}
